import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Spin, Table, Modal, Form, Select, notification } from 'antd';
import { useParams } from 'react-router-dom';
import { authAxios } from '../../services/auth-axios';
import { useAuth } from '../../hooks/AuthProvider';
import dayjs from 'dayjs';

export function TournamentDetails() {
  const { tournamentId } = useParams();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [adminAddModalOpen, setAdminAddModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();

  const isAdmin = localStorage.getItem('roles')?.includes('ADMIN');
  const currentUserId = localStorage.getItem('id');

  const fetchTournamentDetails = async () => {
    setLoading(true);
    try {
      const res = await authAxios().get(`/tournaments/${tournamentId}`);
      setTournament(res.data);
    } catch (error) {
      notification.error({ message: 'Erro ao buscar detalhes do torneio' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendances = async () => {
    try {
      const res = await authAxios().get(`/tournaments/${tournamentId}/attendance`);
      setAttendances(res.data);
    } catch (error) {
      notification.error({ message: 'Erro ao buscar presenças' });
    }
  };

  useEffect(() => {
    fetchTournamentDetails();
    fetchAttendances();
  }, [tournamentId]);

  // Para usuário regular: verifica se ele já confirmou presença
  const userAlreadyAttending = attendances.some(
    (attendance) => attendance.user?.id?.toString() === currentUserId
  );

  // Usuário regular: indica ou cancela a presença usando as rotas padrão
  const handleUserAttendanceToggle = async () => {
    setActionLoading(true);
    try {
      if (userAlreadyAttending) {
        await authAxios().delete(`/tournaments/${tournamentId}/attendance`);
        notification.success({ message: 'Presença cancelada com sucesso!' });
      } else {
        await authAxios().post(`/tournaments/${tournamentId}/attendance`);
        notification.success({ message: 'Presença indicada com sucesso!' });
      }
      fetchAttendances();
    } catch (error) {
      notification.error({
        message: 'Erro ao alterar presença',
        description: error.response?.data?.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Admin: buscar lista de usuários para que possa incluir um usuário ao torneio
  const fetchUsers = async () => {
    try {
      const res = await authAxios().get('/users', { params: { size: 1000 } });
      setUsers(res.data.content);
    } catch (error) {
      notification.error({ message: 'Erro ao buscar usuários' });
    }
  };

  const openAdminModal = () => {
    fetchUsers();
    setAdminAddModalOpen(true);
  };

  // Admin: utiliza rotas específicas para adicionar a presença de um usuário
  const handleAdminAddAttendance = async () => {
    setActionLoading(true);
    try {
      const userId = form.getFieldValue('userId');
      await authAxios().post(`/tournaments/${tournamentId}/player/${userId}`);
      notification.success({ message: 'Presença adicionada com sucesso!' });
      setAdminAddModalOpen(false);
      form.resetFields();
      fetchAttendances();
    } catch (error) {
      notification.error({
        message: 'Erro ao adicionar presença',
        description: error.response?.data?.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Admin: remove a presença utilizando a rota específica para isso
  const handleAdminRemoveAttendance = async (userId) => {
    setActionLoading(true);
    try {
      await authAxios().delete(`/tournaments/${tournamentId}/player/${userId}`);
      notification.success({ message: 'Presença removida com sucesso!' });
      fetchAttendances();
    } catch (error) {
      notification.error({
        message: 'Erro ao remover presença',
        description: error.response?.data?.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const attendanceColumns = [
    {
      title: 'Nome',
      dataIndex: ['user', 'name'],
      key: 'username',
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Button type="primary" danger onClick={() => handleAdminRemoveAttendance(record.user.id)}>
          Remover
        </Button>
      ),
    },
  ];

  if (loading || !tournament) return <Spin size="large" />;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card title="Detalhes do Torneio">
          <p>
            <strong>Nome:</strong> {tournament.name}
          </p>
          <p>
            <strong>Data:</strong> {dayjs(tournament.dateTime).format('DD/MM/YYYY HH:mm')}
          </p>
          <p>
            <strong>Descrição:</strong> {tournament.description}
          </p>
          <p>
            <strong>Status:</strong> {tournament.status}
          </p>
        </Card>
      </Col>
      <Col span={24}>
        <Card title="Presenças">
          {isAdmin ? (
            <>
              <Button type="primary" onClick={openAdminModal} style={{ marginBottom: 16 }}>
                Adicionar Presença
              </Button>
              <Table
                dataSource={attendances}
                columns={attendanceColumns}
                rowKey={(record) => record.user.id}
              />
            </>
          ) : (
            <Button type="primary" onClick={handleUserAttendanceToggle} loading={actionLoading}>
              {userAlreadyAttending ? 'Cancelar Presença' : 'Indicar Presença'}
            </Button>
          )}
        </Card>
      </Col>
      <Modal
        title="Adicionar Presença"
        open={adminAddModalOpen}
        onCancel={() => setAdminAddModalOpen(false)}
        onOk={handleAdminAddAttendance}
        confirmLoading={actionLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Usuário"
            name="userId"
            rules={[{ required: true, message: 'Selecione um usuário' }]}
          >
            <Select
              placeholder="Selecione um usuário"
              options={users.map((user) => ({ label: user.username, value: user.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
}
